import { useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Store, Truck, FileText, Undo2, Tag, Upload, MapPin } from "lucide-react";

const inputCls = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";
const API = process.env.REACT_APP_BACKEND_URL;

const ShipFromCard = ({ shipFrom, onSaved }) => {
  const [form, setForm] = useState(shipFrom);
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(shipFrom), [shipFrom]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/my-store/ship-from", form);
      toast.success("Ship-from address saved — it appears on your labels and slips");
      onSaved();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-6" data-testid="ship-from-card">
      <h3 className="font-semibold font-heading mb-1 flex items-center gap-2"><MapPin size={16} /> Ship-from address</h3>
      <p className="text-xs text-neutral-500 mb-4">Used as the sender on shipping labels and the return address on return slips.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2"><label className="text-xs font-medium text-neutral-600">Full name</label>
          <input data-testid="shipfrom-name" value={form.name || ""} onChange={set("name")} className={inputCls} /></div>
        <div className="sm:col-span-2"><label className="text-xs font-medium text-neutral-600">Address line 1</label>
          <input data-testid="shipfrom-address1" value={form.address1 || ""} onChange={set("address1")} className={inputCls} /></div>
        <div className="sm:col-span-2"><label className="text-xs font-medium text-neutral-600">Address line 2 (optional)</label>
          <input value={form.address2 || ""} onChange={set("address2")} className={inputCls} /></div>
        <div><label className="text-xs font-medium text-neutral-600">City</label>
          <input data-testid="shipfrom-city" value={form.city || ""} onChange={set("city")} className={inputCls} /></div>
        <div><label className="text-xs font-medium text-neutral-600">State</label>
          <input value={form.state || ""} onChange={set("state")} className={inputCls} /></div>
        <div><label className="text-xs font-medium text-neutral-600">ZIP / Postal code</label>
          <input data-testid="shipfrom-zip" value={form.postal_code || ""} onChange={set("postal_code")} className={inputCls} /></div>
        <div><label className="text-xs font-medium text-neutral-600">Country</label>
          <input value={form.country || "United States"} onChange={set("country")} className={inputCls} /></div>
      </div>
      <button data-testid="shipfrom-save" onClick={save} disabled={busy}
        className="mt-4 px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">Save address</button>
    </div>
  );
};

const BookCard = ({ book, onSaved }) => {
  const [form, setForm] = useState({ store_enabled: !!book.store_enabled, store_price: book.store_price ?? "", store_description: book.store_description || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/my-store/projects/${book.id}`, {
        store_enabled: form.store_enabled,
        store_price: form.store_price === "" ? null : parseFloat(form.store_price),
        store_description: form.store_description });
      toast.success(`"${book.title}" listing saved`);
      onSaved();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };
  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/my-store/projects/${book.id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Cover updated");
      onSaved();
    } catch (err) { toast.error(fmtErr(err)); }
    e.target.value = "";
  };
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-6 flex flex-col sm:flex-row gap-5" data-testid={`mystore-book-${book.id}`}>
      <div className="w-24 flex-shrink-0">
        <div className="w-24 h-32 bg-neutral-100 rounded-lg overflow-hidden flex items-center justify-center border border-neutral-200">
          {book.has_cover
            ? <img src={`${API}/api/store/cover/${book.id}?v=${Date.now()}`} alt={book.title} className="w-full h-full object-cover" />
            : <Tag size={18} className="text-neutral-300" />}
        </div>
        <label className="mt-2 flex items-center justify-center gap-1 text-xs font-medium text-neutral-600 border border-neutral-300 rounded-md py-1.5 cursor-pointer hover:bg-neutral-50">
          <Upload size={11} /> Cover
          <input data-testid={`cover-upload-${book.id}`} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={uploadCover} />
        </label>
      </div>
      <div className="flex-1">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold font-heading">{book.title}</h3>
            <p className="text-xs text-neutral-400">{book.units_sold} sold in store · ${Number(book.revenue).toFixed(2)} revenue</p>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input data-testid={`store-toggle-${book.id}`} type="checkbox" checked={form.store_enabled}
              onChange={(e) => setForm((f) => ({ ...f, store_enabled: e.target.checked }))} className="w-4 h-4 accent-neutral-900" />
            Listed in store
          </label>
        </div>
        <div className="grid sm:grid-cols-4 gap-3 mt-3">
          <div><label className="text-xs font-medium text-neutral-600">Retail price ($)</label>
            <input data-testid={`store-price-input-${book.id}`} type="number" step="0.01" min="0" value={form.store_price}
              onChange={(e) => setForm((f) => ({ ...f, store_price: e.target.value }))} className={inputCls} /></div>
          <div className="sm:col-span-3"><label className="text-xs font-medium text-neutral-600">Store description</label>
            <textarea data-testid={`store-desc-input-${book.id}`} rows={2} value={form.store_description}
              onChange={(e) => setForm((f) => ({ ...f, store_description: e.target.value }))} className={inputCls}
              placeholder="A short pitch shown on your store listing..." /></div>
        </div>
        <button data-testid={`mystore-save-${book.id}`} onClick={save} disabled={busy}
          className="mt-3 px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
          {busy ? "Saving..." : "Save listing"}
        </button>
      </div>
    </div>
  );
};

const statusColors = { paid: "bg-amber-50 text-amber-700 border-amber-200", shipped: "bg-emerald-50 text-emerald-700 border-emerald-200" };

export default function MyStore() {
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);

  const load = () => {
    api.get("/my-store").then((r) => setData(r.data));
    api.get("/my-store/orders").then((r) => setOrders(r.data));
  };
  useEffect(load, []);

  const doc = (o, type) => window.open(`${API}/api/my-store/orders/${o.id}/doc/${type}`, "_blank");

  const toggleShipped = async (o) => {
    try {
      const { data: res } = await api.put(`/my-store/orders/${o.id}/ship`);
      toast.success(res.status === "shipped" ? "Marked shipped — buyer notified" : "Marked as not shipped");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!data) return <p className="text-neutral-500">Loading...</p>;

  return (
    <div data-testid="my-store-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-neutral-900 font-heading flex items-center gap-3"><Store size={26} /> My Store</h1>
        <p className="text-neutral-500 mt-1">Manage your listings in the GloriPub bookstore and fulfil your orders.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-10">
        <div className="lg:col-span-2 space-y-4">
          {data.books.length === 0 && (
            <div className="border border-dashed border-neutral-300 rounded-2xl p-12 text-center text-neutral-500 text-sm">
              Your submitted books will appear here, ready to list in the store.
            </div>
          )}
          {data.books.map((b) => <BookCard key={b.id} book={b} onSaved={load} />)}
        </div>
        <ShipFromCard shipFrom={data.ship_from} onSaved={load} />
      </div>

      <h2 className="text-lg font-semibold font-heading mb-4">Store orders</h2>
      {orders.length === 0 ? (
        <div className="border border-dashed border-neutral-300 rounded-2xl p-12 text-center text-neutral-500 text-sm" data-testid="orders-empty">
          No orders yet — when someone buys your book, it shows up here with the buyer's shipping details.
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
            <thead><tr className="bg-neutral-50">
              {["Date", "Book", "Qty", "Your earning", "Ship to", "Status", "Documents"].map((h) => (
                <th key={h} className="px-5 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
            </tr></thead>
            <tbody className="divide-y divide-neutral-100">
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`}>
                  <td className="px-5 py-3 whitespace-nowrap">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3 font-medium text-neutral-900">{o.title}</td>
                  <td className="px-5 py-3">{o.quantity}</td>
                  <td className="px-5 py-3 font-semibold text-emerald-700">${Number(o.author_earning || 0).toFixed(2)}</td>
                  <td className="px-5 py-3 text-neutral-600">
                    {o.shipping ? (
                      <>
                        <p className="font-medium text-neutral-800">{o.shipping.name}</p>
                        <p className="text-xs text-neutral-400">{o.shipping.address1}, {o.shipping.city} {o.shipping.postal_code}</p>
                      </>
                    ) : <span className="text-neutral-300">—</span>}
                  </td>
                  <td className="px-5 py-3">
                    <button data-testid={`ship-toggle-${o.id}`} onClick={() => toggleShipped(o)}
                      className={`text-xs font-medium border rounded-full px-3 py-1 ${statusColors[o.status] || "bg-neutral-50 text-neutral-600 border-neutral-200"}`}>
                      {o.status === "shipped" ? "Shipped ✓" : "Mark shipped"}
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex gap-1.5">
                      <button data-testid={`doc-label-${o.id}`} onClick={() => doc(o, "label")} title="Shipping label"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-neutral-300 rounded-md text-xs font-medium text-neutral-700 hover:bg-neutral-50"><Truck size={12} /> Label</button>
                      <button data-testid={`doc-packing-${o.id}`} onClick={() => doc(o, "packing")} title="Packing slip"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-neutral-300 rounded-md text-xs font-medium text-neutral-700 hover:bg-neutral-50"><FileText size={12} /> Packing</button>
                      <button data-testid={`doc-return-${o.id}`} onClick={() => doc(o, "return")} title="Return slip"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-neutral-300 rounded-md text-xs font-medium text-neutral-700 hover:bg-neutral-50"><Undo2 size={12} /> Return</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}
    </div>
  );
}
